/**
 * hooks/use-ios-install-prompt.ts
 *
 * Detects whether the user is on iOS/iPadOS Safari and the app is NOT yet
 * installed as a PWA (i.e. not running in standalone mode).
 *
 * Returns:
 *   show      — true when the banner should be displayed
 *   dismiss   — call this when the user closes the banner
 *
 * Persists dismissal in localStorage so the banner only shows once per device.
 */
import { useState, useEffect } from 'react'

const DISMISSED_KEY = 'just-us:ios-install-dismissed'

function isIosSafari(): boolean {
  if (typeof window === 'undefined') return false
  const ua = window.navigator.userAgent
  // Matches iPhone, iPad, iPod — but NOT Chrome/Firefox/Edge on iOS
  // (those use 'CriOS', 'FxiOS', 'EdgiOS')
  const isIos = /iphone|ipad|ipod/i.test(ua)
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios|opr\//i.test(ua)
  return isIos && isSafari
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  )
}

export function useIosInstallPrompt() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const alreadyDismissed = localStorage.getItem(DISMISSED_KEY) === '1'
    if (!alreadyDismissed && isIosSafari() && !isStandalone()) {
      // Small delay so the banner doesn't flash on initial paint
      const t = setTimeout(() => setShow(true), 1500)
      return () => clearTimeout(t)
    }
  }, [])

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1')
    setShow(false)
  }

  return { show, dismiss }
}
